import React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useCPFDisplay } from '../hooks/useCPFDisplay';

interface CPFDisplayProps {
  cpf: string | null | undefined;
  className?: string;
  showToggleButton?: boolean;
  // Optional: pass existing hook values to share state with parent component
  hookValues?: ReturnType<typeof useCPFDisplay>;
}

/**
 * Component to display CPF with optional toggle button for admins
 * By default, CPF is masked for everyone. Admins get an eye icon to toggle visibility.
 *
 * @param cpf - The CPF value to display
 * @param className - Optional CSS classes for the container
 * @param showToggleButton - Whether to show the toggle button (default: true)
 * @param hookValues - Optional: reuse hook values from parent component
 */
export const CPFDisplay: React.FC<CPFDisplayProps> = ({
  cpf,
  className = '',
  showToggleButton = true,
  hookValues,
}) => {
  const internalHook = useCPFDisplay();
  const { displayCPF, toggleCPFVisibility, showFullCPF, canToggleCPF } = hookValues || internalHook;

  if (!cpf) {
    return <span className={className}>-</span>;
  }

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      <span>{displayCPF(cpf)}</span>
      {showToggleButton && canToggleCPF && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation(); // Prevent event bubbling to parent elements
            toggleCPFVisibility();
          }}
          className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded hover:bg-gray-100"
          title={showFullCPF ? 'Ocultar CPF' : 'Mostrar CPF'}
          aria-label={showFullCPF ? 'Ocultar CPF' : 'Mostrar CPF'}
        >
          {showFullCPF ? (
            <EyeOff className="w-4 h-4" />
          ) : (
            <Eye className="w-4 h-4" />
          )}
        </button>
      )}
    </div>
  );
};

/**
 * Inline CPF display for use in tables (more compact)
 */
export const CPFDisplayInline: React.FC<{ cpf: string | null | undefined }> = ({ cpf }) => {
  return <CPFDisplay cpf={cpf} className="text-sm" />;
};

/**
 * CPF display without toggle button (always masked)
 */
export const CPFDisplayReadOnly: React.FC<{ cpf: string | null | undefined; className?: string }> = ({
  cpf,
  className = '',
}) => {
  return <CPFDisplay cpf={cpf} className={className} showToggleButton={false} />;
};
